// ============================================================
// ToneSelector.jsx — 보고서 톤(문체) 선택 컴포넌트
// 역할: 사용자가 같은 내용을 어떤 스타일로 작성할지 선택
//       비유: 같은 뉴스를 조선일보/한겨레/타임지 스타일 중 하나로 쓰기
// ============================================================

// 톤별 주의사항 / 안내 메시지 정의
const TONE_NOTES = {
  samsung: {
    type: "tip",
    icon: "🏢",
    text: "삼성/대기업식: 수치와 데이터를 전면에 내세우고, '문제-원인-대책'의 3단 논법과 개조식(~임/~함)을 철저히 준수합니다.",
  },
  bluehouse: {
    type: "tip",
    icon: "🏛️",
    text: "공공기관/청와대식: 정책의 당위성, 이해관계자 의견, 파급 효과를 중시하며, 보수적이고 정제된 어휘를 사용합니다.",
  },
  startup: {
    type: "tip",
    icon: "🚀",
    text: "스타트업/IT식: 한 호흡으로 빠르게 읽히도록 간결한 불렛이나 Action Item 위주로 작성하며, 실용성과 빠른 실행을 강조합니다.",
  },
  global: {
    type: "warning",
    icon: "🌐",
    text: "글로벌/영문식: BLUF(Bottom Line Up Front) 중심의 영문 보고서(Executive Summary) 포맷으로 생성됩니다.",
  },
};

// 4가지 톤 옵션 정의
const TONE_OPTIONS = [
  {
    id: "samsung",       
    icon: "🏢",
    label: "대기업식 (삼성 등)",
    desc: "개조식 · 수치/논리 위주",
    detail: "문제-원인-대책의 명확한 구조화 및 숫자로 증명하는 스타일",
    badge: "권장",
    badgeColor: "bg-blue-500/30 text-blue-300 border-blue-500/40",
    activeStyle: "border-blue-500 bg-blue-500/10 ring-1 ring-blue-500/40",
    hoverStyle: "hover:border-blue-700",
  },
  {
    id: "bluehouse",     
    icon: "🏛️",
    label: "공공기관 (청와대식)",
    desc: "정책적 · 당위성 강조",
    detail: "배경과 명분, 리스크, 종합적 파급 효과를 중시하는 스타일",
    badge: null,
    badgeColor: "",
    activeStyle: "border-slate-500 bg-slate-500/10 ring-1 ring-slate-500/40",
    hoverStyle: "hover:border-slate-700",
  },
  {
    id: "startup",       
    icon: "🚀",
    label: "스타트업 / 실무",
    desc: "실용적 · Action 위주",
    detail: "빠른 의사결정을 위한 직관적이고 군더더기 없는 스타일",
    badge: null,
    badgeColor: "",
    activeStyle: "border-green-500 bg-green-500/10 ring-1 ring-green-500/40",
    hoverStyle: "hover:border-green-700",
  },
  {
    id: "global",      
    icon: "🌐",
    label: "글로벌 (영문)",
    desc: "English · BLUF",
    detail: "외국계 임원·글로벌 보고에 적합. 직설적인 영문 스타일",
    badge: "Beta",
    badgeColor: "bg-purple-500/30 text-purple-300 border-purple-500/40",
    activeStyle: "border-purple-500 bg-purple-500/10 ring-1 ring-purple-500/40",
    hoverStyle: "hover:border-purple-700",
  },
];

/**
 * 보고서 톤 선택 컴포넌트
 * @param {string}   tone     - 현재 선택된 톤 ("samsung" | "bluehouse" | "startup" | "global")
 * @param {Function} onChange - 선택 변경 시 호출되는 함수 (newTone) => void
 */
export default function ToneSelector({ tone, onChange }) {
  return (
    <div className="space-y-2">
      {/* 라벨 */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-slate-400">📄 보고서 톤 (문체) 선택</span>
      </div>

      {/* 옵션 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {TONE_OPTIONS.map((option) => {
          const isActive = tone === option.id;

          return (
            <button
              key={option.id}
              onClick={() => onChange(option.id)}
              title={option.detail}
              className={`
                relative text-left p-3 rounded-xl border transition-all duration-200
                ${isActive
                  ? option.activeStyle           
                  : `border-slate-700 bg-slate-800/40 ${option.hoverStyle}`  
                }
              `}
            >
              {/* 뱃지 */}
              {option.badge && (
                <span className={`absolute -top-1.5 -right-1.5 text-[9px] px-1.5 py-0.5 rounded-full border font-medium ${option.badgeColor}`}>
                  {option.badge}
                </span>
              )}

              {/* 아이콘 + 라벨 */}
              <div className="flex items-center gap-1.5 mb-1 text-nowrap whitespace-nowrap overflow-hidden text-ellipsis">
                <span className="text-sm shrink-0">{option.icon}</span>
                <span className={`text-xs font-semibold truncate ${isActive ? "text-slate-100" : "text-slate-300"}`}>
                  {option.label}
                </span>
              </div>

              {/* 설명 */}
              <p className="text-[10px] text-slate-500 leading-tight">
                {option.desc}
              </p>

              {/* 활성화 체크 */}
              {isActive && (
                <span className="absolute top-2 right-2 text-xs">✓</span>
              )}
            </button>
          );
        })}
      </div>
      
      {/* 선택된 톤 주의사항 */}
      {TONE_NOTES[tone] && (
        <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border text-xs leading-relaxed ${
          TONE_NOTES[tone].type === "warning"
            ? "bg-amber-950/30 border-amber-700/40 text-amber-300"   
            : "bg-slate-800/50 border-slate-700/50 text-slate-500"   
        }`}>
          <span className="flex-shrink-0 mt-0.5">{TONE_NOTES[tone].icon}</span>
          <span>{TONE_NOTES[tone].text}</span>
        </div>
      )}
    </div>
  );
}
